// UsageBlock.tsx — the rail's pinned usage + upgrade card (handoff README §5 "The usage +
// upgrade block"; Dashboard Shell.dc.html's usage-state control bar, which switches this exact
// card between Free / "Paid · quiet" / 92% / 113%). The only always-visible commercial surface
// in the product — before this, a customer's one route to Billing was the avatar dropdown, so a
// Free user at 20% of their cap saw no reason Embent costs money at all.
//
// All the business rules — which tier, which colour, when the CTA appears, when it changes verb
// — live in the pure `usageBlockView` (`../../lib/usage.ts`), tested in `usage.test.ts`. This
// file is pure rendering: it reads `useBillingSummary()` (already shared with the Billing screen
// and `UsageWarningBanner` via one React Query cache entry — this is a third reader, not a new
// fetch) and lays the view object out.
//
// Sizing follows Task 5's `touch?: boolean` prop convention (RailNav.tsx / WorkspaceMenu.tsx) — a
// prop, not a forked file. Content is identical at both sizes; only type size and the CTA's
// height change, matching the one number the README's mobile table actually states for this
// block ("Upgrade button 40px"). Dashboard Shell.dc.html's own mobile-drawer usage-block markup
// additionally drops the pages row, drops the badge-benefit line, and turns the plan-name link
// into a plain span — none of that is written as a rule anywhere in README §5 or Shell
// Anatomy §04 (both describe the block once, with no touch carve-out beyond the button-height
// table entry), so it reads as an abbreviation in a hand-authored demo file rather than an
// intentional content difference. Kept content parity instead; see task-6-report.md.
import { NavLink } from 'react-router';
import { Skeleton } from '@repo/ui/components/skeleton';
import { useBillingSummary } from '../../hooks/useBillingSummary';
import { usageBlockView } from '../../lib/usage';
import { useLocale } from '../../locales/context';

/** One shape per real element, at the real size (handoff README §6) — shown for the rare frame
 * where `useBillingSummary` hasn't resolved yet (in practice the Billing screen or
 * `UsageWarningBanner` has usually already warmed this exact query). */
function UsageBlockSkeleton() {
  return (
    <div className="shrink-0 rounded-[10px] border border-border p-[11px]" aria-hidden="true">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <Skeleton className="h-[13px] w-16" />
        <Skeleton className="h-[11px] w-12" />
      </div>
      <Skeleton className="h-1.5 w-full rounded-full" />
      <Skeleton className="mt-2 h-[11px] w-24" />
    </div>
  );
}

export function UsageBlock({
  touch = false,
  onNavigate,
}: {
  touch?: boolean;
  /** Called when either `NavLink` (plan name or CTA) is actually activated — not when any other
   * part of the card is tapped. `MobileDrawer` wires this to its `onClose` so the drawer closes
   * exactly when a navigation is about to happen, the same "close synchronously in the same
   * click that starts the navigation" contract `RailNav.tsx`'s nav-wide `onClick` relies on.
   * That wrapper-level shortcut isn't safe here: most of this card (the bar, the note, the pages
   * row, the benefit line, the padding) is inert, so a wrapper-level handler would close the
   * drawer on a blank tap with no navigation at all. Omitted on the desktop rail, where there's
   * no drawer to close. */
  onNavigate?: () => void;
}) {
  const { data: summary, isPending } = useBillingSummary();
  const { t, locale } = useLocale();

  if (isPending) return <UsageBlockSkeleton />;
  // Fails quiet, not loud — this block is a convenience surface, not load-bearing for the rest
  // of the shell. A billing-summary fetch error shouldn't take the nav or account row with it.
  if (!summary) return null;

  const view = usageBlockView(summary, t, locale);

  return (
    <div className="shrink-0 rounded-[10px] border bg-card" style={{ padding: '11px 11px 10px', borderColor: view.borderColor }}>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <NavLink
          to="/billing"
          onClick={onNavigate}
          className={({ isActive }) =>
            `truncate font-semibold ${touch ? 'text-[13px]' : 'text-[12.5px]'} ${
              isActive ? 'text-primary' : 'text-foreground hover:text-primary'
            }`
          }
        >
          {view.planName}
        </NavLink>
        {view.bar && (
          <span
            className={`shrink-0 font-mono text-muted-foreground ${touch ? 'text-[11px]' : 'text-[10.5px]'}`}
          >
            {view.bar.label}
          </span>
        )}
      </div>

      {view.bar && (
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none"
            style={{ width: `${view.bar.pct}%`, background: view.bar.color }}
          />
        </div>
      )}

      {view.note && (
        <div className={`mt-1.5 ${touch ? 'text-[12px]' : 'text-[11.5px]'}`} style={{ color: view.noteColor }}>
          {view.note}
        </div>
      )}

      {view.pages && (
        <div className="mt-[9px] border-t border-border pt-[9px]">
          <div className="mb-[5px] flex items-baseline justify-between gap-2">
            <span className={`text-muted-foreground ${touch ? 'text-[12px]' : 'text-[11.5px]'}`}>
              {t.shell.pagesIndexed}
            </span>
            <span className={`shrink-0 font-mono text-muted-foreground ${touch ? 'text-[11px]' : 'text-[10.5px]'}`}>
              {view.pages.label}
            </span>
          </div>
          <div className="h-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none"
              style={{ width: `${view.pages.pct}%`, background: view.pages.color }}
            />
          </div>
        </div>
      )}

      {view.cta && (
        <div className="mt-2.5 flex flex-col gap-[7px] border-t border-border pt-2.5">
          <div
            className={`flex items-start gap-1.5 leading-[1.4] text-muted-foreground ${touch ? 'text-[12px]' : 'text-[11.5px]'}`}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--st-ready)"
              strokeWidth={2.6}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mt-0.5 shrink-0"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <span>{view.cta.benefit}</span>
          </div>
          {/* A `Link`, not a `<button>` with no handler — the CTA's whole job is a faster route
              to `/billing`, the same fix as the plan-name link above; checkout itself (the
              Polar mutation, pending/disabled states, `cancelAtPeriodEnd` gating) already lives
              on the Billing screen and stays there rather than being duplicated here. */}
          <NavLink
            to="/billing"
            onClick={onNavigate}
            className={`flex w-full items-center justify-center rounded-lg bg-primary font-semibold text-primary-foreground shadow-el-1 transition-colors hover:bg-[var(--primary-hover)] ${
              touch ? 'h-10 text-[13.5px]' : 'h-8 text-[12.5px]'
            }`}
          >
            {view.cta.label}
          </NavLink>
        </div>
      )}
    </div>
  );
}
